import { Label, Table } from "@xola/ui-kit";
import { getDemographicCatalog } from "common";
import React, { useMemo } from "react";
import {
    AdjustmentOperation,
    AdjustmentType,
    CatalogItem,
    PartnerDemographicPrice,
    PartnerDemographicTerm,
    Product,
    PurchaseRule,
    PurchaseRuleLink,
    TermsTemplate,
    TermsType,
} from "types";
import { getDiscountPriceSymbol } from "@/components/Agent/TermsTemplate/DemographicTermTable";
import { useFormValues } from "@/components/Agent/TermsTemplate/TermsTemplateFormValues";
import { Currency } from "@/components/Common/Currency";
import { ProductImage } from "@/components/Common/ProductImage";
import { Loader } from "@/components/Loader";
import { useExperiences, useProduct } from "@/hooks/resources/useProduct";
import { getInitialPartnerDemographicPrices } from "@/models/TermsTemplatePurchaseRule";

interface ProductTermsTableProps {
    purchaseRuleLink: ID;
    termsTemplates: PurchaseRuleLink[];
}

export const ProductTermsTable = ({ purchaseRuleLink, termsTemplates }: ProductTermsTableProps) => {
    const { data: experiences, isLoading: isLoadingExperiences } = useExperiences();

    const selectedTermsTemplate = useMemo(() => {
        return termsTemplates.find((template) => template.id === purchaseRuleLink);
    }, [purchaseRuleLink, termsTemplates]);

    const purchaseRules = useMemo(() => {
        return selectedTermsTemplate?.purchaseRules?.map(({ purchaseRule }) => purchaseRule as PurchaseRule) ?? [];
    }, [selectedTermsTemplate]);

    const sellerTerms = useFormValues(purchaseRules, selectedTermsTemplate);

    if (!sellerTerms) {
        return <p>No Seller Terms</p>;
    }

    let productIds = sellerTerms?.products.selected ?? [];

    productIds =
        !sellerTerms?.products.all && sellerTerms?.products.selected?.length
            ? sellerTerms.products.selected ?? []
            : experiences.map((experience) => experience.id);

    if (isLoadingExperiences) {
        return <Loader />;
    }

    if (productIds.length === 0) {
        return null;
    }

    return (
        <div>
            {productIds.length > 0 &&
                productIds.map((productId: ID) => (
                    <DemographicTermsItem key={productId} productId={productId} values={sellerTerms} />
                ))}
        </div>
    );
};

interface DemographicTermsItemProps {
    productId: ID;
    values: TermsTemplate;
}

export const DemographicTermsItem = ({ productId, values }: DemographicTermsItemProps) => {
    const { product, isLoadingProduct } = useProduct(productId);

    if (isLoadingProduct) {
        return <Loader />;
    }

    const partnerDemographicTerm = getPartnerDemographicTerm(values.partnerDemographicTerms, productId);
    const partnerDemographicPrices = product ? getPartnerDemographicPrices(product, partnerDemographicTerm) : [];

    return product ? (
        <>
            <div className="mb-2 flex items-center">
                <div className="flex items-center">
                    <ProductImage
                        product={{ id: product.id, name: product.name, object: product.object }}
                        size="small"
                    />
                    <Label className="no-translate ml-2">{product.name}</Label>
                </div>
            </div>
            <DemographicTermTable partnerDemographicPrices={partnerDemographicPrices} values={values} />
        </>
    ) : null;
};

export const getPartnerDemographicTerm = (partnerDemographicTerms: PartnerDemographicTerm[], productId: ID) => {
    return partnerDemographicTerms.find((term: PartnerDemographicTerm) => term.product.id === productId);
};

export const getPartnerDemographicPrices = (
    product: Product,
    partnerDemographicTerm?: PartnerDemographicTerm,
): PartnerDemographicPrice[] => {
    const demographicCatalogItems: CatalogItem[] = getDemographicCatalog(product);
    const partnerDemographicPrices: PartnerDemographicPrice[] = [];

    if (partnerDemographicTerm?.partnerDemographicPrices?.length) {
        for (const catalogItem of demographicCatalogItems) {
            const matchingPrice = partnerDemographicTerm.partnerDemographicPrices.find(
                (price: PartnerDemographicPrice) => price.template.id === catalogItem.template?.id,
            );
            partnerDemographicPrices.push(getInitialPartnerDemographicPrices(catalogItem, matchingPrice));
        }
    } else {
        for (const catalogItem of demographicCatalogItems) {
            partnerDemographicPrices.push(getInitialPartnerDemographicPrices(catalogItem));
        }
    }

    return partnerDemographicPrices;
};

interface DemographicTermTableProps {
    partnerDemographicPrices: PartnerDemographicPrice[];
    values: TermsTemplate;
}

const DemographicTermTable = ({ partnerDemographicPrices, values }: DemographicTermTableProps) => {
    return (
        <div className="mb-8">
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Name</Table.Header>
                        <Table.Header>Commission</Table.Header>
                        <Table.Header>Adjustment</Table.Header>
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {partnerDemographicPrices?.map((agentDemographicPrice: PartnerDemographicPrice) => {
                        return (
                            <DemographicItem
                                key={agentDemographicPrice.template.id}
                                agentDemographicPrice={agentDemographicPrice}
                                values={values}
                            />
                        );
                    })}
                </Table.Body>
            </Table>
        </div>
    );
};

interface DemographicItemProps {
    agentDemographicPrice: PartnerDemographicPrice;
    values: TermsTemplate;
}

export const DemographicItem = ({ agentDemographicPrice, values }: DemographicItemProps) => {
    const { discount, commission } = agentDemographicPrice;
    const demographicName = commission.catalogItem?.name;
    const commissionTermValue =
        values.termsType === TermsType.BASIC ? values.commission.amount ?? 0 : commission.adjustmentAmount ?? 0;
    const discountTermValue =
        values.termsType === TermsType.BASIC ? values.discount.amount ?? 0 : discount.adjustmentAmount ?? 0;

    return (
        <Table.Row key={agentDemographicPrice.template.id} className="justify-between border-b border-gray-lighter">
            <Table.Cell className="w-[40%]">{demographicName}</Table.Cell>
            <Table.Cell className="w-[30%]">
                <TermValue termAlgorithm={commission.adjustmentType} termValue={commissionTermValue} />
            </Table.Cell>
            <Table.Cell className="w-[30%]">
                {getDiscountPriceSymbol(discount)}
                <TermValue termAlgorithm={discount.adjustmentType} termValue={discountTermValue} />
                {discount.adjustmentOperation === AdjustmentOperation.FINAL && " (Final price)"}
            </Table.Cell>
        </Table.Row>
    );
};

interface TermValueProps {
    termAlgorithm?: AdjustmentType;
    termValue?: number;
}

export const TermValue = ({ termAlgorithm, termValue }: TermValueProps) => {
    return termAlgorithm === AdjustmentType.ABSOLUTE ? <Currency>{termValue}</Currency> : <>{termValue}%</>;
};
